/**
 * HomePage component - Main dashboard for daily wellness check-ins.
 * Material Design themed with elegant cards and styling.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetTodayCheckin, useCreateCheckin } from '../services/api';
import CheckinForm from '../components/checkin/CheckinForm';
import RecommendationCard from '../components/checkin/RecommendationCard';
import BOSIScoreDisplay from '../components/guidance/BOSIScore';
import GuidanceSection from '../components/guidance/GuidanceSection';
import ActionPlanDisplay from '../components/guidance/ActionPlan';
import FeedbackLoop from '../components/guidance/FeedbackLoop';
import WeeklySummaryDisplay from '../components/guidance/WeeklySummary';
import { useToast, ToastContainer } from '../components/Toast';
import { useAuth } from '../contexts/AuthContext';
import { CheckInInputs, DailyGuidance, WeeklySummary } from '../types';

const BODY_STATE_LABELS: Record<string, string> = {
  recovery: 'Recovery Mode',
  balanced: 'Balanced',
  high_energy: 'High Energy',
  hormonal_dip: 'Hormonal Dip',
  hormonal_shift: 'Hormonal Shift'
};

const BODY_STATE_COLORS: Record<string, string> = {
  recovery: 'bg-accent-100 text-accent-800 border-accent-300',
  balanced: 'bg-green-100 text-green-800 border-green-300',
  high_energy: 'bg-blue-100 text-blue-800 border-blue-300',
  hormonal_dip: 'bg-red-100 text-red-800 border-red-300',
  hormonal_shift: 'bg-primary-200 text-primary-900 border-primary-400'
};

const BODY_STATE_DESCRIPTIONS: Record<string, string> = {
  recovery: 'Your body needs rest and recovery today. Focus on gentle movement and self-care.',
  balanced: 'You\'re in a balanced state. Maintain your routine with moderate activity.',
  high_energy: 'You\'re feeling great! This is a good day for challenging workouts.',
  hormonal_dip: 'Hormonal fluctuations are affecting you. Be gentle with yourself today.',
  hormonal_shift: 'You\'re experiencing hormonal changes. Adapt your routine accordingly.'
};

const BODY_STATE_ICONS: Record<string, string> = {
  recovery: '🌱',
  balanced: '⚖️',
  high_energy: '⚡',
  hormonal_dip: '🌊',
  hormonal_shift: '🔄'
};

const GOAL_LABELS: Record<string, string> = {
  lose_weight: 'Weight Management',
  lose_belly_fat: 'Belly Fat Reduction',
  build_strength: 'Strength Building',
  improve_energy: 'Energy & Vitality',
};

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: todayCheckin, isLoading, isError } = useGetTodayCheckin();
  const createCheckin = useCreateCheckin();
  const [error, setError] = useState<string>('');
  const { toasts, closeToast, success, error: showError } = useToast();

  const handleCheckinSubmit = async (inputs: CheckInInputs) => {
    try {
      setError('');
      await createCheckin.mutateAsync(inputs);
      success('Check-in completed successfully! Your personalized wellness guidance is ready.');
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      let errorMsg: string;
      if (Array.isArray(detail)) {
        errorMsg = detail.map((e: any) => e.msg || String(e)).join('; ');
      } else if (typeof detail === 'string') {
        errorMsg = detail;
      } else {
        errorMsg = 'Failed to submit check-in. Please try again.';
      }
      setError(errorMsg);
      showError(errorMsg);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary-200 border-t-primary-600 mb-4"></div>
        <p className="text-neutral-600 font-medium">Loading your wellness dashboard...</p>
      </div>
    );
  }

  // Show check-in form if no check-in today
  if (isError || !todayCheckin) {
    return (
      <>
        <ToastContainer toasts={toasts} onClose={closeToast} />
        <div className="max-w-3xl mx-auto animate-fade-in">
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-primary-800 mb-4">
            Good Morning! 🌅
          </h1>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto leading-relaxed">
            Start your day with a 2-minute check-in to receive personalized,
            evidence-based wellness guidance tailored to your body's needs.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-5 bg-red-50 border-l-4 border-red-400 rounded-r-xl animate-slide-down">
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        )}

        {createCheckin.isError && !error && (
          <div className="mb-6 p-5 bg-red-50 border-l-4 border-red-400 rounded-r-xl animate-slide-down">
            <p className="text-sm text-red-800 font-medium">
              Failed to submit check-in. Please try again.
            </p>
          </div>
        )}

        <CheckinForm
          onSubmit={handleCheckinSubmit}
          isLoading={createCheckin.isPending}
        />
      </div>
      </>
    );
  }

  // Extract checkin and additional data from response
  const checkin = todayCheckin.checkin || todayCheckin; // Handle both old and new API response structure
  const dailyGuidance: DailyGuidance | undefined = checkin.daily_guidance;

  // Show completed check-in with recommendations
  const bodyState = checkin.body_state;
  const stateLabel = BODY_STATE_LABELS[bodyState.state_type] || bodyState.state_type;
  const stateColor = BODY_STATE_COLORS[bodyState.state_type] || 'bg-primary-100 text-primary-800 border-primary-300';

  // If daily_guidance is available, render the new structured guidance view
  if (dailyGuidance) {
    return (
      <>
        <ToastContainer toasts={toasts} onClose={closeToast} />
        <div className="max-w-3xl mx-auto animate-fade-in">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start sm:items-center justify-between mb-3 gap-2">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-primary-800">
                Today's Wellness Guidance
              </h1>
              <button
                onClick={() => navigate('/checkin/edit')}
                className="flex-shrink-0 px-3 py-2 bg-white border-2 border-primary-600 text-primary-700 font-semibold rounded-xl hover:bg-primary-50 transition-all duration-200 shadow-soft hover:shadow-md flex items-center space-x-1.5 text-sm min-h-[44px]"
                aria-label="Edit today's check-in"
              >
                <span>✏️</span>
                <span className="hidden sm:inline">Edit</span>
              </button>
            </div>
            <p className="text-neutral-600 text-sm">
              {new Date(checkin.completed_at).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}
              {' '}&middot;{' '}
              {new Date(checkin.completed_at).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit'
              })}
              {checkin.edit_count > 0 && (
                <span className="ml-2 text-neutral-500">
                  (edited {checkin.edit_count} {checkin.edit_count === 1 ? 'time' : 'times'})
                </span>
              )}
            </p>
          </div>

          {/* Goal Display */}
          {user?.profile?.primary_goal ? (
            <div className="mb-4 flex items-center space-x-2 text-sm text-neutral-600">
              <span className="font-medium text-[#B7A76D]">Goal:</span>
              <span>{GOAL_LABELS[user.profile.primary_goal] || user.profile.primary_goal}</span>
              {user.profile.physical_profile?.target_weight && (
                <span className="text-neutral-500">
                  &middot; Lose {user.profile.physical_profile.target_weight.value}
                  {user.profile.physical_profile.target_weight.unit}
                </span>
              )}
              {user.profile.physical_profile?.target_waist && (
                <span className="text-neutral-500">
                  &middot; -{user.profile.physical_profile.target_waist.value}
                  {user.profile.physical_profile.target_waist.unit} waist
                </span>
              )}
            </div>
          ) : (
            <div className="mb-4">
              <button
                onClick={() => navigate('/profile')}
                className="text-sm text-[#B7A76D] hover:text-[#9E8F5A] font-medium underline underline-offset-2"
              >
                Set your goal →
              </button>
            </div>
          )}

          {/* BOSI Score */}
          <div className="card shadow-elegant mb-6 overflow-hidden">
            <div className="flex items-center justify-between">
              <BOSIScoreDisplay score={dailyGuidance.bosi_score} />
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${stateColor}`}>
                {stateLabel}
              </span>
            </div>
          </div>

          {/* Structured Guidance Sections */}
          <div className="space-y-4 mb-8">
            {/* Observation */}
            <GuidanceSection icon="👁️" title="BOSI Sees You">
              <p>{dailyGuidance.observation}</p>
            </GuidanceSection>

            {/* Signal of the Day */}
            <GuidanceSection icon="📡" title="Signal of the Day" variant="highlight">
              <p className="font-medium">{dailyGuidance.signal_of_the_day}</p>
            </GuidanceSection>

            {/* Body Insight */}
            <GuidanceSection icon="🧬" title="Your Body Today">
              <p>{dailyGuidance.body_insight}</p>
            </GuidanceSection>

            {/* Consequence */}
            <GuidanceSection icon="⚡" title="What This Means">
              <p>{dailyGuidance.body_consequence}</p>
            </GuidanceSection>

            {/* Action Plan */}
            <div className="rounded-xl p-5 bg-gradient-to-br from-primary-50 to-white border border-primary-200">
              <div className="flex items-start space-x-3 mb-4">
                <span className="text-2xl flex-shrink-0">🎯</span>
                <h3 className="text-lg font-semibold text-neutral-900">What To Do Today</h3>
              </div>
              <ActionPlanDisplay plan={dailyGuidance.action_plan} />
            </div>

            {/* Reassurance */}
            <GuidanceSection icon="🤗" title="Remember" variant="subtle">
              <p className="italic">{dailyGuidance.reassurance}</p>
            </GuidanceSection>

            {/* Pattern Insight (conditional) */}
            {dailyGuidance.pattern_insight && (
              <GuidanceSection icon="📊" title="Pattern Insight">
                <p>{dailyGuidance.pattern_insight}</p>
              </GuidanceSection>
            )}

            {/* Trust Nudge (conditional) */}
            {dailyGuidance.trust_nudge && (
              <div className="rounded-xl p-5 bg-gradient-to-br from-amber-50 to-white border border-amber-200">
                <div className="flex items-start space-x-3">
                  <span className="text-2xl flex-shrink-0">💛</span>
                  <p className="text-neutral-700 leading-relaxed italic">
                    {dailyGuidance.trust_nudge}
                  </p>
                </div>
              </div>
            )}

            {/* Feedback Loop */}
            <FeedbackLoop
              checkinId={checkin._id}
              existingFeedback={checkin.guidance_feedback}
            />
          </div>

          {/* Weekly Summary (conditional - every 7th consecutive day) */}
          {todayCheckin.weekly_summary && (
            <div className="mb-8">
              <WeeklySummaryDisplay summary={todayCheckin.weekly_summary as WeeklySummary} />
            </div>
          )}

          {/* Check-in Summary (collapsed) */}
          <div className="card bg-gradient-to-br from-neutral-50 to-white border border-neutral-200 shadow-soft">
            <h3 className="text-lg font-serif font-semibold text-neutral-900 mb-4">
              Today's Check-In Summary
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-neutral-600 text-xs font-medium mb-1">Sleep</div>
                <div className="text-sm font-semibold text-primary-700 capitalize">
                  {typeof checkin.inputs.sleep_quality === 'number'
                    ? `${checkin.inputs.sleep_quality}/5`
                    : checkin.inputs.sleep_quality}
                </div>
              </div>
              <div className="text-center">
                <div className="text-neutral-600 text-xs font-medium mb-1">Energy</div>
                <div className="text-sm font-semibold text-neutral-800 capitalize">
                  {checkin.inputs.energy_level}
                </div>
              </div>
              <div className="text-center">
                <div className="text-neutral-600 text-xs font-medium mb-1">Activity</div>
                <div className="text-sm font-semibold text-neutral-800 capitalize">
                  {checkin.inputs.activity_type
                    ? checkin.inputs.activity_type.replace(/_/g, ' ')
                    : 'None'}
                </div>
              </div>
              <div className="text-center">
                <div className="text-neutral-600 text-xs font-medium mb-1">Symptoms</div>
                <div className="text-sm font-semibold text-neutral-700">
                  {checkin.inputs.body_symptoms.includes('none')
                    ? '0'
                    : checkin.inputs.body_symptoms.length}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Fallback: Legacy view for check-ins without daily_guidance
  const stateDescription = BODY_STATE_DESCRIPTIONS[bodyState.state_type] || '';
  const stateIcon = BODY_STATE_ICONS[bodyState.state_type] || '💫';

  return (
    <>
      <ToastContainer toasts={toasts} onClose={closeToast} />
      <div className="max-w-5xl mx-auto animate-fade-in">
        {/* Header */}
      <div className="mb-10">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-4xl font-serif font-bold text-primary-800">
            Today's Wellness Guidance
          </h1>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/checkin/edit')}
              className="px-5 py-2.5 bg-white border-2 border-primary-600 text-primary-700 font-semibold rounded-xl hover:bg-primary-50 transition-all duration-200 shadow-soft hover:shadow-md flex items-center space-x-2"
              aria-label="Edit today's check-in"
            >
              <span>✏️</span>
              <span>Edit Check-In</span>
            </button>
            <span className="text-sm text-neutral-500 font-medium">
              {new Date(checkin.completed_at).toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric'
              })}
            </span>
          </div>
        </div>
        <p className="text-neutral-600">
          Check-in completed at {new Date(checkin.completed_at).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit'
          })}
          {checkin.edit_count > 0 && (
            <span className="ml-2 text-sm text-neutral-500">
              (edited {checkin.edit_count} {checkin.edit_count === 1 ? 'time' : 'times'})
            </span>
          )}
        </p>
      </div>

      {/* Body State Card */}
      <div className="card shadow-elegant mb-10 overflow-hidden">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <span className="text-4xl">{stateIcon}</span>
            <h2 className="text-2xl font-serif font-bold text-neutral-900">Your Body State</h2>
          </div>
          <span className={`text-sm font-semibold px-5 py-2.5 rounded-full border-2 ${stateColor}`}>
            {stateLabel}
          </span>
        </div>

        <p className="text-neutral-700 text-lg mb-6 leading-relaxed">{stateDescription}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-br from-primary-50 to-primary-100 p-5 rounded-xl border border-primary-200">
            <div className="text-neutral-600 text-sm font-medium mb-2">Physical Recovery</div>
            <div className="flex items-baseline space-x-2">
              <div className="text-4xl font-bold text-primary-700">
                {Math.round(bodyState.scores.physical_recovery)}
              </div>
              <span className="text-neutral-500 font-medium">/ 100</span>
            </div>
            <div className="mt-3 bg-white rounded-full h-2 overflow-hidden">
              <div
                className="bg-primary-600 h-full transition-all duration-500 rounded-full"
                style={{ width: `${bodyState.scores.physical_recovery}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-accent-50 to-accent-100 p-5 rounded-xl border border-accent-200">
            <div className="text-neutral-600 text-sm font-medium mb-2">Energy & Mood</div>
            <div className="flex items-baseline space-x-2">
              <div className="text-4xl font-bold text-accent-700">
                {Math.round(bodyState.scores.energy_mood)}
              </div>
              <span className="text-neutral-500 font-medium">/ 100</span>
            </div>
            <div className="mt-3 bg-white rounded-full h-2 overflow-hidden">
              <div
                className="bg-accent-600 h-full transition-all duration-500 rounded-full"
                style={{ width: `${bodyState.scores.energy_mood}%` }}
              ></div>
            </div>
          </div>
        </div>

        {bodyState.scores.hormonal_indicators.length > 0 && (
          <div className="mt-6 p-4 bg-primary-50 border border-primary-200 rounded-xl">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">🔬</span>
              <div>
                <p className="text-sm font-semibold text-primary-900 mb-1">
                  Hormonal Indicators Detected
                </p>
                <p className="text-sm text-primary-800">
                  {bodyState.scores.hormonal_indicators.map((indicator: string) =>
                    indicator.replace(/_/g, ' ')
                  ).join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recommendations */}
      <div className="mb-10">
        <div className="mb-6">
          <h2 className="text-3xl font-serif font-bold text-neutral-900 mb-3">
            Your Personalized Recommendations
          </h2>
          <p className="text-neutral-600 text-lg">
            Based on your check-in, here are {checkin.recommendations.filter((r: any) => r.category !== 'note').length} evidence-based
            recommendations to support your wellness today.
          </p>
        </div>

        {/* Limited Data Note */}
        {checkin.recommendations.some((r: any) => r.category === 'note') && (
          <div className="mb-6 p-5 bg-blue-50 border-l-4 border-blue-400 rounded-r-xl animate-slide-down">
            <div className="flex items-start space-x-3">
              <span className="text-2xl">💡</span>
              <div>
                <p className="text-sm font-semibold text-blue-900 mb-1">
                  {checkin.recommendations.find((r: any) => r.category === 'note')?.text}
                </p>
                <p className="text-sm text-blue-800">
                  {checkin.recommendations.find((r: any) => r.category === 'note')?.explanation}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {checkin.recommendations.filter((r: any) => r.category !== 'note').map((rec: any, index: number) => (
            <div key={index} className="animate-slide-up" style={{ animationDelay: `${index * 100}ms` }}>
              <RecommendationCard recommendation={rec} />
            </div>
          ))}
        </div>
      </div>

      {/* Check-in Summary */}
      <div className="card bg-gradient-to-br from-primary-50 to-white border border-primary-200 shadow-soft">
        <h3 className="text-xl font-serif font-semibold text-neutral-900 mb-5">
          Today's Check-In Summary
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="text-neutral-600 text-sm font-medium mb-2">Sleep</div>
            <div className="text-lg font-semibold text-primary-700 capitalize">
              {typeof checkin.inputs.sleep_quality === 'number'
                ? `${checkin.inputs.sleep_quality}/5`
                : checkin.inputs.sleep_quality}
            </div>
            {checkin.inputs.wake_feeling && (
              <div className="text-xs text-neutral-500 mt-1 capitalize">
                Woke {checkin.inputs.wake_feeling}
              </div>
            )}
          </div>
          <div className="text-center">
            <div className="text-neutral-600 text-sm font-medium mb-2">Energy & Mood</div>
            <div className="text-lg font-semibold text-neutral-800 capitalize">
              {checkin.inputs.energy_level}
            </div>
            {checkin.inputs.mood && (
              <div className="text-xs text-neutral-500 mt-1 capitalize">
                {checkin.inputs.mood.replace(/_/g, ' ')}
              </div>
            )}
          </div>
          <div className="text-center">
            <div className="text-neutral-600 text-sm font-medium mb-2">Activity</div>
            <div className="text-lg font-semibold text-neutral-800 capitalize">
              {checkin.inputs.activity_type
                ? checkin.inputs.activity_type.replace(/_/g, ' ')
                : 'Not tracked'}
            </div>
            {checkin.inputs.activity_intensity && (
              <div className="text-xs text-neutral-500 mt-1 capitalize">
                {checkin.inputs.activity_intensity}
                {checkin.inputs.activity_duration ? ` / ${checkin.inputs.activity_duration}min` : ''}
              </div>
            )}
          </div>
          <div className="text-center">
            <div className="text-neutral-600 text-sm font-medium mb-2">Symptoms</div>
            <div className="text-3xl font-bold text-neutral-700">
              {checkin.inputs.body_symptoms.includes('none')
                ? '0'
                : checkin.inputs.body_symptoms.length}
            </div>
            {checkin.inputs.waist_feeling && checkin.inputs.waist_feeling !== 'normal' && (
              <div className="text-xs text-neutral-500 mt-1 capitalize">
                Waist: {checkin.inputs.waist_feeling}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default HomePage;
